// DAS-2162: Other Investments tab – CRUD table for savings, deposits, real estate, etc.
// DAS-2175: Savings rate monitor — per-account rate change detection
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Tooltip,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, Link,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';  // DAS-2556
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
// DAS-2427: market-wide interest-opportunities sub-modal (top savings banks
// + best deposito per 3M/6M/9M/1Y).
import InterestOpportunitiesModal from './InterestOpportunitiesModal';

const ASSET_CLASSES = ['Equity', 'Fixed Income', 'Cash', 'Real Assets', 'Alternatives', 'Crypto'];

const ASSET_CLASS_COLORS = {
  'Equity':        '#1976d2',
  'Fixed Income':  '#7b1fa2',
  'Cash':          '#2e7d32',
  'Real Assets':   '#e65100',
  'Alternatives':  '#455a64',
  'Crypto':        '#f57c00',
};

const EMPTY_FORM = {
  asset_class: '',
  amount: '',
  interest_rate: '',
  expiry_date: '',
  institution: '',
  url: '',           // DAS-2556: optional institution link
  description: '',
};

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtTimestamp = (ts) => {
  if (!ts) return '—';
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  return date.toLocaleDateString('nl-NL', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const OtherInvestmentsTab = ({ onAlertCount }) => {
  const userId = getUserId();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // DAS-2427: state for the market-wide Interest Opportunities sub-modal.
  const [opportunitiesOpen, setOpportunitiesOpen] = useState(false);

  const [orderBy, setOrderBy] = useState('updated_at');
  const [orderDir, setOrderDir] = useState('desc');

  // DAS-2175: Rate change detection state
  const [rateMonitor, setRateMonitor] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const fetchRateMonitor = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/savings_rate_monitor/${userId}`);
      setRateMonitor(res.data || null);
    } catch (err) {
      console.error('[DAS-2175] rate monitor fetch error:', err);
    }
  }, [userId]);

  useEffect(() => { fetchRateMonitor(); }, [fetchRateMonitor]);

  useEffect(() => {
    const count = rateMonitor?.user_accounts?.filter(a => a.rate_changed).length || 0;
    if (onAlertCount) onAlertCount(count);
  }, [rateMonitor, onAlertCount]);

  const getAccountMonitor = (record) => {
    if (!rateMonitor?.user_accounts) return null;
    return rateMonitor.user_accounts.find(a => a.id === record.id) || null;
  };

  const handleConfirmRate = async () => {
    if (!confirmTarget) return;
    setConfirming(true);
    try {
      await api.put(`/savings_rate_confirm/${confirmTarget.id}`, {
        userid: parseInt(userId),
        interest_rate: confirmTarget.current_market_rate,
      });
      setConfirmTarget(null);
      await fetchRecords();
      await fetchRateMonitor();
    } catch (err) {
      console.error('[DAS-2175] confirm rate error:', err);
      setError('Failed to update rate.');
    } finally {
      setConfirming(false);
    }
  };

  const fetchRecords = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/other_investments/${userId}`);
      setRecords(res.data || []);
    } catch (err) {
      console.error('[DAS-2162] fetch error:', err);
      setError('Failed to load other investments.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSort = (field) => {
    setOrderDir(prev => (orderBy === field && prev === 'desc') ? 'asc' : 'desc');
    setOrderBy(field);
  };

  const sorted = [...records].sort((a, b) => {
    const dir = orderDir === 'asc' ? 1 : -1;
    const av = a[orderBy] ?? '';
    const bv = b[orderBy] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv);
    return dir * String(av).localeCompare(String(bv));
  });

  // DAS-2427 follow-up: group rows by Asset Class, and within Cash by
  // deposito vs non-deposito. Header rows are inserted between groups; the
  // existing TableSortLabel-driven sort continues to apply WITHIN each
  // group, so the user's preferred sort is preserved per-group.
  //
  // Deposito classifier: `expiry_date IS NOT NULL`. Structural / reliable:
  // deposito's by definition have a maturity date; current/savings
  // accounts don't. We deliberately do NOT match on the description text
  // ("deposito" / "depo …") because some of Hans's existing records use
  // short labels ("depo 2") that text-matching would miss while the
  // expiry-date column catches them correctly. If you ever record a
  // deposito without its maturity date, it'll fall into the non-deposito
  // bucket — fix the row, don't relax this classifier.
  const ASSET_CLASS_ORDER = ['Cash', 'Fixed Income', 'Equity', 'Real Assets', 'Alternatives', 'Crypto'];
  const isCashDeposito = (r) =>
    r.asset_class === 'Cash' && r.expiry_date != null && String(r.expiry_date).trim() !== '';

  const groups = (() => {
    const byClass = {};
    for (const r of sorted) {
      const ac = r.asset_class || 'Other';
      if (!byClass[ac]) byClass[ac] = [];
      byClass[ac].push(r);
    }
    const orderedKeys = [
      ...ASSET_CLASS_ORDER.filter((k) => byClass[k]),
      ...Object.keys(byClass).filter((k) => !ASSET_CLASS_ORDER.includes(k)),
    ];
    const out = [];
    for (const ac of orderedKeys) {
      const rows = byClass[ac];
      if (ac === 'Cash') {
        const dep = rows.filter(isCashDeposito);
        const non = rows.filter((r) => !isCashDeposito(r));
        if (dep.length) {
          out.push({
            key: 'Cash:Deposito',
            label: 'Cash — Deposito (fixed term)',
            color: ASSET_CLASS_COLORS.Cash,
            rows: dep,
          });
        }
        if (non.length) {
          out.push({
            key: 'Cash:Non-Deposito',
            label: 'Cash — Non-deposito (savings / current accounts)',
            color: ASSET_CLASS_COLORS.Cash,
            rows: non,
          });
        }
      } else {
        out.push({
          key: ac,
          label: ac,
          color: ASSET_CLASS_COLORS[ac] || '#999',
          rows,
        });
      }
    }
    return out;
  })();

  const openAdd = () => {
    setEditRecord(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    const monitor = getAccountMonitor(record);
    if (monitor?.rate_changed) {
      setConfirmTarget(monitor);
      return;
    }
    setEditRecord(record);
    setForm({
      asset_class: record.asset_class || '',
      amount: record.amount != null ? String(record.amount) : '',
      interest_rate: record.interest_rate != null ? String(record.interest_rate) : '',
      expiry_date: record.expiry_date || '',
      institution: record.institution || '',
      url: record.url || '',           // DAS-2556
      description: record.description || '',
    });
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.asset_class || !form.amount) return;
    setSaving(true);
    try {
      const payload = {
        userid: parseInt(userId),
        asset_class: form.asset_class,
        amount: parseFloat(form.amount),
        interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
        expiry_date: form.expiry_date || null,
        institution: form.institution || null,
        url: form.url ? form.url.trim() : null,   // DAS-2556
        description: form.description || null,
      };

      if (editRecord) {
        await api.put(`/other_investments/${editRecord.id}`, payload);
      } else {
        await api.post('/other_investments', payload);
      }
      setDialogOpen(false);
      await fetchRecords();
      await fetchRateMonitor();
    } catch (err) {
      console.error('[DAS-2162] save error:', err);
      setError('Failed to save record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/other_investments/${deleteTarget.id}?userid=${userId}`);
      setDeleteTarget(null);
      await fetchRecords();
    } catch (err) {
      console.error('[DAS-2162] delete error:', err);
      setError('Failed to delete record.');
    } finally {
      setDeleting(false);
    }
  };

  // DAS-2255: Toggle is_ignored / is_liquid flags
  const handleToggleFlag = async (recordId, field) => {
    try {
      await api.patch(`/other_investments/${recordId}/toggle`, {
        userid: parseInt(userId),
        field,
      });
      await fetchRecords();
      if (field === 'is_ignored') await fetchRateMonitor();
    } catch (err) {
      console.error(`[DAS-2255] toggle ${field} error:`, err);
      setError(`Failed to toggle ${field}.`);
    }
  };

  const totalValue = records.reduce((sum, r) => sum + (r.amount || 0), 0);
  const yearlyInterest = records
    .filter(r => r.asset_class === 'Cash' && r.interest_rate > 0 && r.amount > 0)
    .reduce((sum, r) => sum + (r.amount * r.interest_rate / 100), 0);
  const changesDetected = rateMonitor?.user_accounts?.filter(a => a.rate_changed).length || 0;
  const hasMonitor = rateMonitor && rateMonitor.savings_count > 0;

  // DAS-2255: Liquid cash total (only records flagged as liquid)
  const liquidTotal = records
    .filter(r => r.is_liquid && r.amount > 0)
    .reduce((sum, r) => sum + (r.amount || 0), 0);

  // DAS-2255: Institution exposure (group by institution)
  const institutionExposure = {};
  records.forEach(r => {
    const inst = r.institution || 'Unknown';
    institutionExposure[inst] = (institutionExposure[inst] || 0) + (r.amount || 0);
  });
  const exposureEntries = Object.entries(institutionExposure).sort((a, b) => b[1] - a[1]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Other Investments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Savings accounts, deposits, real estate and other non-broker assets
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {/* DAS-2255: Liquid cash badge */}
          {liquidTotal > 0 && (
            <Tooltip title="Total directly available (liquid) cash — toggle per record with the droplet icon">
              <Chip
                icon={<WaterDropIcon />}
                label={`Liquid: ${fmtCurrency(liquidTotal)}`}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#0288d1', borderColor: '#0288d1', '& .MuiChip-icon': { color: '#0288d1' } }}
              />
            </Tooltip>
          )}
          {/* DAS-2255: Institution exposure */}
          {exposureEntries.length > 0 && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Exposure per institution</Typography>
                  {exposureEntries.map(([inst, amount]) => (
                    <Typography key={inst} variant="caption" display="block">
                      {inst}: {fmtCurrency(amount)}
                    </Typography>
                  ))}
                </Box>
              }
            >
              <Chip
                icon={<AccountBalanceIcon />}
                label={`${exposureEntries.length} institution${exposureEntries.length !== 1 ? 's' : ''}`}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.9rem', cursor: 'default' }}
              />
            </Tooltip>
          )}
          {yearlyInterest > 0 && (
            <Tooltip title="Sum of (amount x interest %) for all savings accounts and deposits">
              <Chip
                label={`Yearly interest: ${fmtCurrency(yearlyInterest)}`}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#2e7d32', borderColor: '#2e7d32' }}
              />
            </Tooltip>
          )}
          {records.length > 0 && (
            <Chip
              label={`Total: ${fmtCurrency(totalValue)}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.9rem' }}
            />
          )}
          {/* DAS-2427: Opens InterestOpportunitiesModal showing top market
              rates for immediate-access savings (Savings_Rate_Market) and
              best rate per 3M/6M/9M/1Y deposito term (Spaarinformatie_Data).
              Read-only market overview; no per-row matching here (that's
              what the existing Market Rate column does). */}
          <Button
            variant="outlined"
            onClick={() => setOpportunitiesOpen(true)}
            sx={{ textTransform: 'none' }}
            title="See top market rates for savings + deposito (3M/6M/9M/1Y)"
          >
            💰 Interest Opportunities
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={openAdd}
            sx={{ textTransform: 'none' }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* DAS-2175: Rate change alert banner */}
      {changesDetected > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
          <strong>{changesDetected} savings account{changesDetected > 1 ? 's' : ''}</strong> may have a changed interest rate.
          Review the "Market Rate" column and confirm updates where applicable.
        </Alert>
      )}

      {records.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No other investments recorded yet.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={openAdd}
            sx={{ textTransform: 'none' }}
          >
            Add your first investment
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { id: 'asset_class', label: 'Asset Class', align: 'left' },
                  { id: 'amount', label: 'Amount (EUR)', align: 'right' },
                  { id: 'interest_rate', label: 'Interest %', align: 'right' },
                  { id: 'expiry_date', label: 'Expiry Date', align: 'left' },
                  { id: 'institution', label: 'Institution', align: 'left' },
                  { id: 'description', label: 'Description', align: 'left' },
                  { id: 'updated_at', label: 'Last Edit', align: 'left' },
                ].map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {hasMonitor && (
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="Current savings rate for this bank on spaarrente.nl. Only for savings accounts (no expiry date).">
                      <span>Market Rate</span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, width: 160 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* DAS-2427 follow-up: render rows grouped by Asset Class
                  (and within Cash by deposito / non-deposito). Each group
                  starts with a header TableRow spanning all columns; data
                  rows below it keep the existing per-row JSX unchanged. */}
              {groups.map((group) => {
                const groupSum = group.rows.reduce(
                  (s, r) => s + (Number(r.amount) || 0),
                  0,
                );
                return (
                  <React.Fragment key={group.key}>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell
                        colSpan={hasMonitor ? 9 : 8}
                        sx={{
                          borderLeft: `4px solid ${group.color}`,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          py: 0.6,
                          color: 'text.primary',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{group.label}</span>
                          <Chip
                            label={`${group.rows.length} record${group.rows.length === 1 ? '' : 's'}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Box
                            sx={{
                              marginLeft: 'auto',
                              position: 'sticky',
                              right: 8,
                              bgcolor: '#f5f5f5',
                              pl: 1,
                              color: 'text.secondary',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            }}
                          >
                            {fmtCurrency(groupSum)}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {group.rows.map((row) => {
                      const monitor = hasMonitor ? getAccountMonitor(row) : null;
                      return (
                        <TableRow key={row.id} hover sx={row.is_ignored ? { opacity: 0.5 } : undefined}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: ASSET_CLASS_COLORS[row.asset_class] || '#999',
                            flexShrink: 0,
                          }}
                        />
                        {row.asset_class}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {fmtCurrency(row.amount)}
                    </TableCell>
                    <TableCell align="right">
                      {row.interest_rate != null ? `${row.interest_rate}%` : '—'}
                    </TableCell>
                    <TableCell>{fmtDate(row.expiry_date)}</TableCell>
                    <TableCell>
                      {/* DAS-2556: clickable institution link (opens in new tab) when a
                          valid http(s) URL is set; only http(s) is linkified (XSS guard). */}
                      {row.institution
                        ? (row.url && /^https?:\/\//i.test(row.url)
                            ? (
                                <Link
                                  href={row.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                  {row.institution}
                                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Link>
                              )
                            : row.institution)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {row.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtTimestamp(row.updated_at)}
                      </Typography>
                    </TableCell>
                    {hasMonitor && (
                      <TableCell align="center">
                        {monitor == null ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : monitor.current_market_rate == null ? (
                          <Tooltip title={`No match found on spaarrente.nl for "${monitor.institution}"`}>
                            <Typography variant="caption" color="text.secondary">No match</Typography>
                          </Tooltip>
                        ) : monitor.rate_changed ? (
                          <Box>
                            <Tooltip title={`${monitor.matched_bank}: rate is now ${monitor.current_market_rate}% (you have ${monitor.stored_rate}%). Click to review.`}>
                              <Chip
                                size="small"
                                icon={<WarningAmberIcon />}
                                label={`${monitor.current_market_rate}%`}
                                onClick={() => setConfirmTarget(monitor)}
                                sx={{
                                  bgcolor: '#ed6c0218',
                                  color: '#ed6c02',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  borderColor: '#ed6c02',
                                  cursor: 'pointer',
                                  '& .MuiChip-icon': { color: '#ed6c02', fontSize: '1rem' },
                                }}
                                variant="outlined"
                              />
                            </Tooltip>
                            <Typography variant="caption" display="block" sx={{ color: '#ed6c02', mt: 0.3, fontSize: '0.65rem' }}>
                              Changed ({monitor.delta > 0 ? '+' : ''}{monitor.delta.toFixed(2)}%)
                            </Typography>
                          </Box>
                        ) : (
                          <Tooltip title={`${monitor.matched_bank}: ${monitor.current_market_rate}% — matches your stored rate`}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem', color: '#2e7d32' }} />
                              <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                {monitor.current_market_rate}%
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={row.is_ignored ? 'Ignored — click to include in rate comparison' : 'Click to ignore (exclude from rate comparison)'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleFlag(row.id, 'is_ignored')}
                          sx={{ color: row.is_ignored ? '#bdbdbd' : '#616161' }}
                        >
                          {row.is_ignored ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.is_liquid ? 'Liquid — click to unmark' : 'Click to mark as liquid (directly available) cash'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleFlag(row.id, 'is_liquid')}
                          sx={{ color: row.is_liquid ? '#0288d1' : '#bdbdbd' }}
                        >
                          <WaterDropIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtCurrency(totalValue)}
                </TableCell>
                <TableCell colSpan={hasMonitor ? 7 : 6} />
              </TableRow>
            </TableBody>
          </MuiTable>
        </TableContainer>
      )}

      {/* DAS-2175: source attribution */}
      {hasMonitor && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
          Market rates: spaarrente.nl (savings only){rateMonitor.last_scraped ? ` | ${rateMonitor.last_scraped.substring(0, 10)}` : ''}
          {rateMonitor.ecb_current != null ? ` | ECB DFR: ${rateMonitor.ecb_current}%` : ''}
        </Typography>
      )}

      {/* DAS-2175: Rate change confirmation dialog */}
      <Dialog open={!!confirmTarget} onClose={() => setConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rate Change Detected</DialogTitle>
        {confirmTarget && (
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              <strong>{confirmTarget.institution}</strong>
              {confirmTarget.matched_bank !== confirmTarget.institution && (
                <Typography variant="caption" color="text.secondary" component="span">
                  {' '}(matched: {confirmTarget.matched_bank})
                </Typography>
              )}
            </Typography>
            <Box display="flex" gap={4} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Your stored rate</Typography>
                <Typography variant="h6">{confirmTarget.stored_rate}%</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current market rate</Typography>
                <Typography variant="h6" sx={{ color: '#ed6c02' }}>
                  {confirmTarget.current_market_rate}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Change</Typography>
                <Typography variant="h6" sx={{ color: confirmTarget.delta > 0 ? '#2e7d32' : '#d32f2f' }}>
                  {confirmTarget.delta > 0 ? '+' : ''}{confirmTarget.delta.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              Please verify this rate change with your bank before confirming.
              This will update your stored interest rate from {confirmTarget.stored_rate}% to {confirmTarget.current_market_rate}%.
            </Alert>
            {/* DAS-2613: the market rate above is the regular ongoing rate. If a
                temporary new-customer actierente exists, show it separately so it
                isn't mistaken for the rate this (existing-customer) account earns. */}
            {confirmTarget.promo_rate != null && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {confirmTarget.matched_bank} currently advertises a promotional rate of{' '}
                <strong>{confirmTarget.promo_rate}%</strong>
                {confirmTarget.promo_ends ? ` (until ${confirmTarget.promo_ends})` : ''}, but that
                is a temporary new-customer offer — not the {confirmTarget.current_market_rate}% ongoing
                rate above. Don't set your stored rate to the promo figure.
              </Alert>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setConfirmTarget(null)} sx={{ textTransform: 'none' }}>
            Dismiss
          </Button>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={() => {
                const rec = records.find(r => r.id === confirmTarget.id);
                setConfirmTarget(null);
                if (rec) {
                  setEditRecord(rec);
                  setForm({
                    asset_class: rec.asset_class || '',
                    amount: rec.amount != null ? String(rec.amount) : '',
                    interest_rate: rec.interest_rate != null ? String(rec.interest_rate) : '',
                    expiry_date: rec.expiry_date || '',
                    institution: rec.institution || '',
                    description: rec.description || '',
                  });
                  setDialogOpen(true);
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Edit Manually
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmRate}
              disabled={confirming}
              sx={{ textTransform: 'none' }}
            >
              {confirming ? <CircularProgress size={20} /> : 'Confirm & Update Rate'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRecord ? 'Edit Investment' : 'Add Investment'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Asset Class"
              value={form.asset_class}
              onChange={handleFormChange('asset_class')}
              required
              fullWidth
            >
              {ASSET_CLASSES.map(ac => (
                <MenuItem key={ac} value={ac}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ASSET_CLASS_COLORS[ac] }} />
                    {ac}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Amount (EUR)"
              type="number"
              value={form.amount}
              onChange={handleFormChange('amount')}
              required
              fullWidth
              inputProps={{ step: '0.01', min: 0 }}
            />

            <TextField
              label="Interest Rate (%)"
              type="number"
              value={form.interest_rate}
              onChange={handleFormChange('interest_rate')}
              fullWidth
              inputProps={{ step: '0.01' }}
              helperText="Optional — for savings accounts and deposits"
            />

            <TextField
              label="Expiry Date"
              type="date"
              value={form.expiry_date}
              onChange={handleFormChange('expiry_date')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional — for fixed deposits"
            />

            <TextField
              label="Institution"
              value={form.institution}
              onChange={handleFormChange('institution')}
              fullWidth
              placeholder="e.g. ING, ABN AMRO, Rabobank"
            />

            {/* DAS-2556: optional link, shared across all accounts at this institution —
                clicking the institution name in the table opens it in a new tab. */}
            <TextField
              label="Institution URL"
              value={form.url}
              onChange={handleFormChange('url')}
              fullWidth
              placeholder="e.g. https://www.ing.nl"
              helperText="Optional — shared across all your accounts at this institution; opens when you click the institution name"
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={handleFormChange('description')}
              fullWidth
              multiline
              rows={2}
              placeholder="Short description of this investment"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.asset_class || !form.amount}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={20} /> : (editRecord ? 'Save Changes' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.description || deleteTarget?.asset_class}</strong>
            {deleteTarget?.institution ? ` at ${deleteTarget.institution}` : ''}
            {' '}({fmtCurrency(deleteTarget?.amount)})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DAS-2427: Market-wide interest opportunities sub-modal. Mounted at
          the bottom so it overlays the tab content; opens only on demand
          (no data fetch until the user clicks). */}
      <InterestOpportunitiesModal
        visible={opportunitiesOpen}
        onClose={() => setOpportunitiesOpen(false)}
        userId={userId}
      />
    </Box>
  );
};

export default OtherInvestmentsTab;
